import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { useAdminToggleMutation } from "@/lib/hooks/useAdminToggleMutation";
import {
  approvePriceLimitBypass,
  rejectPriceLimitBypass,
  setPriceLimitBypass,
  getSettings,
} from "@/lib/api/admin";
import type { Store } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { BYPASS_MAX_PRICE_DOLLARS } from "@/lib/constants";
import { formatDateTime } from "@/lib/utils/date";
import { DollarSign } from "lucide-react";
import { AdminOverrideCard } from "@/components/AdminOverrideCard";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type StorePriceLimitSectionProps = {
  store: Store;
};

export function StorePriceLimitSection({ store }: StorePriceLimitSectionProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const defaultMax = (settings?.default_max_price_cents ?? 30000) / 100;
  const nightclubMax = (settings?.nightclub_max_price_cents ?? 50000) / 100;
  const storeMax = store.category === "nightclub" ? nightclubMax : defaultMax;

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["store", store.id] });
  }

  const approveMutation = useMutation({
    mutationFn: async () => approvePriceLimitBypass(user!, store.id),
    onSuccess: () => {
      invalidate();
      toast.success("Price limit bypass approved.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const rejectMutation = useMutation({
    mutationFn: async () => rejectPriceLimitBypass(user!, store.id),
    onSuccess: () => {
      invalidate();
      toast.success("Price limit bypass rejected.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const toggleMutation = useAdminToggleMutation<boolean>(
    store.id,
    setPriceLimitBypass,
    "Price limit bypass updated.",
    () => setConfirmOpen(false)
  );

  const hasPendingRequest = store.price_limit_bypass_requested_at && !store.price_limit_bypass;

  return (
    <>
      <AdminOverrideCard
        icon={DollarSign}
        title="Price Limit"
        statusLabel={store.price_limit_bypass ? "Bypass Active" : "Standard Limits"}
        statusDescription={
          store.price_limit_bypass
            ? `Up to $${BYPASS_MAX_PRICE_DOLLARS.toLocaleString()}`
            : `Up to $${storeMax.toLocaleString()}`
        }
        action={
          store.price_limit_bypass
            ? {
                label: "Revoke Bypass",
                onClick: () => setConfirmOpen(true),
                disabled: toggleMutation.isPending,
              }
            : !hasPendingRequest
              ? {
                  label: "Grant Bypass",
                  onClick: () => setConfirmOpen(true),
                  disabled: toggleMutation.isPending,
                }
              : undefined
        }
      >
        {hasPendingRequest && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3">
            <p className="text-xs font-medium text-yellow-800">Pending request</p>
            <p className="text-xs text-yellow-700">
              Requested {formatDateTime(store.price_limit_bypass_requested_at!)}
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-destructive"
                onClick={() => rejectMutation.mutate()}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
      </AdminOverrideCard>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={store.price_limit_bypass ? "Revoke price limit bypass" : "Grant price limit bypass"}
        description={
          store.price_limit_bypass
            ? `Revoke the price limit bypass for "${store.name}"? Item prices will be capped back to $${storeMax.toLocaleString()}.`
            : `Grant "${store.name}" a price limit bypass, allowing items up to $${BYPASS_MAX_PRICE_DOLLARS.toLocaleString()}?`
        }
        confirmLabel={store.price_limit_bypass ? "Revoke" : "Grant"}
        destructive={store.price_limit_bypass}
        isPending={toggleMutation.isPending}
        onConfirm={() => toggleMutation.mutate(!store.price_limit_bypass)}
      />
    </>
  );
}
